// ProvisioningDefaults.tsx — the defaults store, surface grid + per-surface detail.
//
// TWO DIFFERENT THINGS LIVE ON THIS SCREEN AND THEY MUST NOT BE CONFUSED:
//
//   1. The STORE (`PUT /provisioning-defaults/:section`) — whole-section read and
//      write. This is persistent state shared by every future provisioning run.
//   2. Each item's own stored `selected` flag — the include layer that provisioning
//      actually honours (services/provisioningDefaults.js:44 and :289).
//
// Edits apply to customers provisioned AFTER the edit. Existing customers are
// untouched — that is a property of the store, not an oversight.
//
// 2026-07-28 CORRECTION. This screen previously rendered a column of per-item
// "Include" checkboxes above a banner asserting that they "become the Setup
// Wizard's `selections` payload and are applied at provision time". That was not
// true, and it was the most dangerous kind of wrong sentence: an inert control
// with a confident label on a settings screen.
//   - The checkbox state lived in local component state and was never sent
//     anywhere, never persisted, and discarded on unmount.
//   - SetupWizard.tsx:87-89 builds its own `selections` from its own state, keyed
//     by SECTION, never per item.
//   - What provisioning honours per item is the STORED `selected` flag, which the
//     checkbox neither read nor wrote.
// The checkboxes are therefore replaced by a read-only rendering of the stored
// flag, which is the value that actually decides.
//
// 2026-07-28, SECOND CORRECTION — and the reason this paragraph is being edited
// rather than left alone. It used to end: "The only editor that ever wrote that
// flag (DefaultsEditor.tsx, mounted at PlatformConsole.tsx:525/656) is no longer
// reachable after the console cutover … The screen now says so rather than
// implying an edit is possible."
//
// That was true when it was written and is now false. The editor is reachable
// again — platform/DefaultsEditor.tsx, opened by "Edit defaults" below — so the
// sentence had to go with the change that made it untrue, not at some later
// point when somebody noticed. A comment asserting that no edit is possible,
// sitting above a button that performs the edit, is the same class of defect as
// the inert checkbox this file's own header was written to record.
//
// This screen remains the READ-ONLY overview: the place to read what a setting
// does. Changing what gets copied happens in the editor.
//
// Inline help copy lives in settingsHelp.ts, keyed by section and field, derived
// from the code that consumes each setting. See the header of that file.

import React, { useCallback, useEffect, useState } from "react";
import { PT } from "../../platformTheme";
import { pget, pmutate, perr } from "./api";
import {
  PCard, PSection, PBtn, PBadge, PBanner, PEmpty, PSpinner, PTable, PRow,
  PReasonPrompt, PGrid, th, td, fmtDate,
} from "./ui";
import {
  sectionHelp, STATUS_LABEL, STATUS_TONE, UNDETERMINED,
  type FieldHelp,
} from "./settingsHelp";
import { DefaultsEditor } from "./DefaultsEditor";

interface Section { section: string; payload: any[]; updated_at?: string }

// ── Inline help: one line always visible, the rest on request ────────────────
// Deliberately local to this screen rather than added to ui.tsx: it is one shape
// used in one place, and ui.tsx is explicit that it is not a component library.

const labelCol: React.CSSProperties = {
  width: 170, flexShrink: 0, color: PT.txD, fontSize: 11.5, fontWeight: 600,
  textTransform: "uppercase", letterSpacing: 0.4,
};

const FieldHelpRow: React.FC<{ name: string; help: FieldHelp }> = ({ name, help }) => {
  const [open, setOpen] = useState(false);
  const detail = help.status === "undetermined" ? [UNDETERMINED, ...help.detail] : help.detail;
  return (
    <div style={{ borderBottom: `1px solid ${PT.brdSubtle}` }}>
      <button
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        style={{
          display: "flex", alignItems: "baseline", gap: 9, width: "100%", textAlign: "left",
          background: "transparent", border: "none", cursor: "pointer",
          fontFamily: PT.fontUI, padding: "10px 12px",
        }}
      >
        <span style={{ color: PT.txD, fontSize: 10, width: 9, flexShrink: 0 }}>{open ? "▾" : "▸"}</span>
        <span style={{ fontSize: 12.5, fontWeight: 600, color: PT.tx, flexShrink: 0 }}>{help.label}</span>
        <PBadge tone={STATUS_TONE[help.status]}>{STATUS_LABEL[help.status]}</PBadge>
        <span style={{ fontSize: 12, color: PT.txM, flex: 1 }}>{help.summary}</span>
      </button>
      {open && (
        <div style={{ padding: "0 12px 13px 30px", fontSize: 12.5, color: PT.txM, lineHeight: 1.55 }}>
          {help.statusNote && (
            <div style={{ fontSize: 12, color: PT.txD, marginBottom: 8, fontStyle: "italic" }}>
              {help.statusNote}
            </div>
          )}
          {detail.map((p, i) => <p key={i} style={{ margin: "0 0 8px" }}>{p}</p>)}
          {help.values && help.values.length > 0 && (
            <div style={{ margin: "0 0 8px" }}>
              {help.values.map((v) => (
                <div key={v.value} style={{ display: "flex", gap: 9, marginBottom: 5 }}>
                  <div style={{ width: 170, flexShrink: 0, color: PT.tx, fontWeight: 600 }}>{v.value}</div>
                  <div style={{ flex: 1 }}>{v.effect}</div>
                </div>
              ))}
            </div>
          )}
          <div style={{ display: "flex", gap: 9, marginTop: 9 }}>
            <div style={labelCol}>Default</div>
            <div style={{ flex: 1 }}>{help.fallback}</div>
          </div>
          {help.interacts && (
            <div style={{ display: "flex", gap: 9, marginTop: 6 }}>
              <div style={labelCol}>Affects</div>
              <div style={{ flex: 1 }}>{help.interacts}</div>
            </div>
          )}
          <div style={{ marginTop: 9, fontSize: 11, color: PT.txD, fontFamily: PT.fontMono }}>{name}</div>
        </div>
      )}
    </div>
  );
};

export const ProvisioningDefaults: React.FC = () => {
  const [sections, setSections] = useState<Section[] | null>(null);
  const [source, setSource] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [err, setErr] = useState("");
  const [msg, setMsg] = useState("");
  const [prompt, setPrompt] = useState<null | { title: string; detail: React.ReactNode; danger?: boolean; run: (r: string) => Promise<void> }>(null);
  const [busy, setBusy] = useState(false);
  const [pErr, setPErr] = useState("");
  // 2026-07-28: the sectioned editor is reachable again. See DefaultsEditor.tsx
  // for what it is and why it is a port. This screen stays the read-only
  // overview — the place you go to READ what a setting does — and hands off to
  // the editor for changing what gets copied.
  const [editing, setEditing] = useState(false);

  // Refresh reports its own state and stamps a time. A re-read that produces the
  // same rows is otherwise indistinguishable from a button that is not wired.
  const [reloading, setReloading] = useState(false);
  const [at, setAt] = useState<Date | null>(null);

  const load = useCallback(async () => {
    setErr(""); setReloading(true);
    const r = await pget("/provisioning-defaults");
    setReloading(false); setAt(new Date());
    if (!r.ok) { setErr(perr(r, "Could not load provisioning defaults")); setSections([]); return; }
    setSections(r.data?.sections || []);
    setSource(r.data?.default_source_tenant || null);
  }, []);
  useEffect(() => { load(); }, [load]);

  const run = async (reason: string) => {
    if (!prompt) return;
    setBusy(true); setPErr("");
    try { await prompt.run(reason); setPrompt(null); await load(); }
    catch (e: any) { setPErr(e?.message || "Action failed"); }
    finally { setBusy(false); }
  };

  const current = sections?.find((s) => s.section === open) || null;

  // ─── EVERY HOOK MUST BE CALLED ABOVE THIS LINE ─────────────────────────────
  // The `if (editing)` early return used to sit HERE, above the two useMemo
  // calls further down. That is what threw React error #300 ("Rendered fewer
  // hooks than expected") the moment anyone clicked "Edit defaults": the first
  // render ran 11 hooks, and setting `editing` made the next render return
  // early after 9. React compares the counts and throws, and the error boundary
  // blanked the whole screen — so no default could be edited at all.
  //
  // The pre-redesign console never had this bug because it rendered the editor
  // INLINE rather than returning early — `{defOpen && <DefaultsEditor …/>}`,
  // PlatformConsole.tsx:696 at commit 905991f64. Every conditional return in
  // this component now sits below all hooks, which restores that property.
  //
  // If you add a hook to this component, add it ABOVE this comment. A hook
  // added below it will reintroduce exactly this crash, and it will look fine
  // until someone clicks the button.

  // The value provisioning actually honours: an item is copied unless its stored
  // include flag is explicitly false (services/provisioningDefaults.js:44).
  const isIncluded = (item: any) => !(item && item.selected === false);

  // ── Item naming ────────────────────────────────────────────────────────────
  //
  // 2026-07-28. This screen listed "Item 1 … Item 19" for routing rules and
  // "Item 1 … Item 11" for parent requirements, and the operator reported it as
  // a lost feature. It was: the generic fallback below tried name/title/label/
  // slug/key and NONE of those columns exists on those sections. Routing rules
  // are keyed by `agreement_type`, parent requirements by
  // `agreement_category` + `child_type`, draft upload rules by `draft_label`,
  // SLA targets by `agreement_type`, password policy by nothing at all — so
  // every row on five of the ten surfaces fell through to its index.
  //
  // The section-aware naming below is the one DefaultsEditor.tsx carried
  // (itemLabel(), keyOf()) and that the console cutover at c2c8528a dropped.
  // Ported as plain strings because this screen is read-only.
  //
  // The generic fallback is KEPT as the last resort, not removed: a new section
  // added to the store should render something rather than crash. But it is now
  // genuinely last, and a section reaching it is a signal that this map needs an
  // entry — which is why the fallback says so.
  const SECTION_LABEL: Record<string, (it: any) => string | null> = {
    roles: (it) => it.display_name || it.slug || null,
    contract_types: (it) => it.name || null,
    routing_rules: (it) => it.agreement_type || null,
    draft_upload_rules: (it) => it.draft_label || null,
    sla_targets: (it) => it.agreement_type || "(all agreement types)",
    parent_requirements: (it) =>
      it.agreement_category && it.child_type ? `${it.agreement_category} / ${it.child_type}` : null,
    extraction_profiles: (it) => it.name || null,
    business_units: (it) => it.name || null,
    prompt_templates: (it) => it.name || null,
    password_policy: () => "Password policy",
  };

  const label = (section: string, item: any, i: number): string => {
    const named = SECTION_LABEL[section]?.(item);
    if (named) return named;
    const generic = item?.name || item?.title || item?.label || item?.slug || item?.key;
    if (generic) return String(generic);
    return `Unnamed entry ${i + 1}`;
  };

  // A one-line human summary per section. The old column printed the first three
  // raw JSON keys, which is legible to whoever wrote the schema and to nobody
  // else — and it is the column an operator would use to tell two rules apart.
  const SECTION_DETAIL: Record<string, (it: any) => string> = {
    roles: (it) => `${(it.permissions || []).length} permissions${it.is_system ? " · system role" : ""}`,
    contract_types: (it) =>
      [it.category, it.short_code, it.is_amendment_style ? "amendment-style" : null]
        .filter(Boolean).join(" · "),
    routing_rules: (it) =>
      `goes to ${it.primary_route || "—"}${it.primary_role ? ` (${it.primary_role})` : ""}` +
      (it.secondary_route ? `, then ${it.secondary_route}${it.secondary_role ? ` (${it.secondary_role})` : ""}` : ""),
    draft_upload_rules: (it) =>
      `sets status ${it.target_status || "—"}${it.routing_aware ? " · routing-aware" : ""}` +
      (it.authorized_roles ? ` · roles: ${it.authorized_roles}` : " · any role"),
    sla_targets: (it) => `${it.target_days ?? "—"} days${it.severity ? ` · ${it.severity}` : ""}`,
    parent_requirements: (it) =>
      `needs a parent of: ${(it.required_parent_types || []).join(", ") || "—"}` +
      (it.online_terms_eligible ? " · online terms allowed" : ""),
    extraction_profiles: (it) => `${(it.fields || []).length} fields${it.is_system ? " · system" : ""}`,
    business_units: (it) => (it.parent_name ? `under ${it.parent_name}` : "top level"),
    prompt_templates: (it) =>
      [it.category, it.is_global ? "global" : null, it.inject_as_system ? "system-injected" : null]
        .filter(Boolean).join(" · "),
    password_policy: (it) =>
      `at least ${it.min_length} characters` +
      [it.require_upper ? "an uppercase letter" : null, it.require_digit ? "a digit" : null,
       it.require_special ? "a special character" : null].filter(Boolean).join(", ").replace(/^(.)/, ", must include $1"),
  };

  // ── Cross-surface dependency (the read-only half of the old cascade) ───────
  //
  // Routing rules, parent requirements and SLA targets all reference a CONTRACT
  // TYPE by name. If that type is not copied into a new customer, the rule that
  // names it lands inert — it can never match anything. DefaultsEditor.tsx
  // computed this live while you toggled (grayReason/typeRef, plus the server's
  // conservative name-matcher in utils/typeMatch.js) and the cutover dropped it
  // along with the editor.
  //
  // What is restored here is the FACT, not the toggling: this screen cannot
  // change a selection, so it shows which entries reference a type that is not
  // being copied. Deliberately EXACT-match only — the server's fuzzy candidate
  // map is not fetched by this screen, and guessing a match here would be a
  // second, quieter implementation of matching logic that could disagree with
  // the one provisioning actually uses. An exact miss is reported; anything
  // else is left alone rather than asserted.
  const copiedTypeNames = React.useMemo(() => {
    const ct = sections?.find((s) => s.section === "contract_types")?.payload || [];
    return new Set(ct.filter(isIncluded).map((t: any) => String(t.name || "").toLowerCase()));
  }, [sections]);
  const knownTypeNames = React.useMemo(() => {
    const ct = sections?.find((s) => s.section === "contract_types")?.payload || [];
    return new Set(ct.map((t: any) => String(t.name || "").toLowerCase()));
  }, [sections]);

  /** Contract types this entry names that exist in the catalog but are NOT copied. */
  const droppedRefs = (section: string, item: any): string[] => {
    const refs: string[] =
      section === "routing_rules" ? [item.agreement_type]
      : section === "sla_targets" ? [item.agreement_type]
      : section === "parent_requirements" ? [item.child_type, ...(item.required_parent_types || [])]
      : [];
    return refs
      .filter((n) => n && knownTypeNames.has(String(n).toLowerCase()))
      .filter((n) => !copiedTypeNames.has(String(n).toLowerCase()))
      .map(String);
  };

  const detail = (section: string, item: any): string => {
    const fn = SECTION_DETAIL[section];
    if (fn) { try { return fn(item) || ""; } catch { /* fall through */ } }
    return typeof item === "object"
      ? Object.entries(item).filter(([k]) => !["name", "title", "label", "selected"].includes(k))
          .slice(0, 3).map(([k, v]) => `${k}=${typeof v === "object" ? "…" : String(v)}`).join("  ")
      : String(item);
  };

  // Safe here: below every hook. Same position as the `open && current` branch
  // that has always worked.
  if (editing) {
    return (
      <PSection title="Provisioning defaults · edit" sub={source ? `source: ${source}` : undefined}>
        <DefaultsEditor onDone={() => { setEditing(false); load(); }} />
      </PSection>
    );
  }

  if (open && current) {
    const help = sectionHelp(current.section);
    const total = current.payload?.length || 0;
    const inc = (current.payload || []).filter(isIncluded).length;
    return (
      <PSection
        title={current.section.replace(/_/g, " ")}
        sub={`${inc} of ${total} copied into a new customer`}
        right={<PBtn small onClick={() => setOpen(null)}>← All surfaces</PBtn>}
      >
        {help && (
          <PCard style={{ padding: 0, marginBottom: 14 }}>
            <div style={{ padding: "12px 14px", borderBottom: `1px solid ${PT.brdSubtle}` }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: PT.tx, marginBottom: 6 }}>
                What these settings do
              </div>
              {help.detail.map((p, i) => (
                <p key={i} style={{ margin: "0 0 7px", fontSize: 12.5, color: PT.txM, lineHeight: 1.55 }}>{p}</p>
              ))}
              <div style={{ fontSize: 11.5, color: PT.txD, marginTop: 4 }}>
                Each line below was written from the code that reads the setting. Open one for what
                happens when it is on, when it is off, what it defaults to, and what else it changes.
              </div>
            </div>
            {Object.entries(help.fields).map(([name, f]) => (
              <FieldHelpRow key={name} name={name} help={f} />
            ))}
          </PCard>
        )}

        <PBanner tone="info">
          Whether an entry is copied into a new customer is decided by its own stored setting, shown
          in the first column and read-only <em>on this screen</em>. Use <strong>Edit defaults</strong>
          to change it. The Setup Wizard can leave out a whole area, but not individual entries.
        </PBanner>

        <PCard style={{ padding: 0 }}>
          {total === 0 ? <PEmpty>This surface has no items.</PEmpty> : (
            <PTable
              minWidth={560}
              head={<><th style={{ ...th, width: 90 }}>Copied</th><th style={th}>Item</th><th style={th}>Detail</th></>}
            >
              {(current.payload || []).map((item, i) => (
                <PRow key={i}>
                  <td style={td}>
                    {isIncluded(item)
                      ? <PBadge tone="ok">Yes</PBadge>
                      : <PBadge tone="warn">No</PBadge>}
                  </td>
                  <td style={{ ...td, fontWeight: 600 }}>{label(current.section, item, i)}</td>
                  <td style={{ ...td, fontSize: 12, color: PT.txM }}>
                    {detail(current.section, item)}
                    {droppedRefs(current.section, item).map((n) => (
                      <div key={n} style={{ marginTop: 4 }}>
                        <PBadge tone="warn">
                          references “{n}”, which is not copied
                        </PBadge>
                      </div>
                    ))}
                  </td>
                </PRow>
              ))}
            </PTable>
          )}
        </PCard>
      </PSection>
    );
  }

  return (
    <PSection
      title="Provisioning defaults"
      sub={[source ? `source: ${source}` : null, at ? `updated ${at.toLocaleTimeString()}` : null]
        .filter(Boolean).join(" · ") || undefined}
      right={
        <>
          {/* Both halves are wanted: the busy-reporting Refresh that landed on
              main, and the Edit defaults entry point this branch adds. */}
          <PBtn small disabled={reloading} onClick={load}>{reloading ? "Refreshing…" : "Refresh"}</PBtn>{" "}
          <PBtn small v="primary" onClick={() => setEditing(true)}>Edit defaults</PBtn>{" "}
          <PBtn
            small v="danger"
            onClick={() => setPrompt({
              title: "Reseed provisioning defaults",
              danger: true,
              detail: (
                <>
                  Replaces <strong>every section</strong> in the defaults store from the Bootstrap
                  tenant, discarding edits made here. Customers already provisioned are unaffected.
                </>
              ),
              run: async (reason) => {
                const r = await pmutate("POST", "/provisioning-defaults/reseed", reason);
                if (!r.ok) throw new Error(perr(r));
                setMsg("Defaults reseeded from the source tenant.");
              },
            })}
          >Reseed from source</PBtn>
        </>
      }
    >
      <PBanner>{err}</PBanner>
      <PBanner tone="ok">{msg}</PBanner>
      <PBanner tone="info">
        Edits here apply to customers provisioned <strong>after</strong> the edit. Existing
        customers are not retro-updated. Open a surface to read what each of its settings does.
      </PBanner>

      {sections === null ? <PSpinner /> : sections.length === 0 ? (
        <PCard><PEmpty>No provisioning defaults are configured.</PEmpty></PCard>
      ) : (
        <PGrid min={260}>
          {sections.map((s) => {
            const h = sectionHelp(s.section);
            return (
              <PCard key={s.section} onClick={() => setOpen(s.section)} title="Open surface">
                <div style={{ fontWeight: 600, fontSize: 13, marginBottom: 7 }}>
                  {s.section.replace(/_/g, " ")}
                </div>
                <PBadge tone="neutral">{(s.payload || []).length} items</PBadge>
                {h && (
                  <div style={{ fontSize: 12, color: PT.txM, marginTop: 9, lineHeight: 1.5 }}>
                    {h.summary}
                  </div>
                )}
                {s.updated_at && (
                  <div style={{ fontSize: 11, color: PT.txD, marginTop: 8 }}>updated {fmtDate(s.updated_at)}</div>
                )}
              </PCard>
            );
          })}
        </PGrid>
      )}

      <PReasonPrompt
        open={!!prompt}
        title={prompt?.title || ""}
        detail={prompt?.detail}
        danger={prompt?.danger}
        busy={busy}
        error={pErr}
        onCancel={() => setPrompt(null)}
        onConfirm={run}
      />
    </PSection>
  );
};
